/**
 * KANON `Cwiczenia` 17.09.2026, variant **e · Tabela + panel** — `/cwiczenia`, and only that.
 *
 * Composition only. `useExerciseLibrary` owns the read and the view state, `entities/exercise-library`
 * owns every rule about what an exercise IS, `model/` owns every decision this screen makes, and
 * this file decides which of six things to show under a header, a toolbar and a scope switch that
 * render in all six.
 *
 * ── Two panes, and the right one stands from the FIRST frame ────────────────────────────────
 * The table's card and the 396px preview column. With no second child in the pane the table is
 * `flex: 1` against nothing and takes the whole measure, so a panel arriving would narrow every
 * column by 396px plus the gap — measured on the clients roster at exactly that. The column is
 * therefore drawn in every state the panel is meant to be visible in: its skeleton while the
 * catalogue is on its way, the movement itself once it has landed.
 *
 * ── The entrance is a two-item cascade, once, on arrival ────────────────────────────────────
 * From the shared motion dictionary, with `reducedMotion` passed IN — the single CSS block in
 * `tokens/base.css` cannot reach a Motion animation, and a surface that relied on it would ignore
 * the preference while looking compliant. Neither pane is keyed, so a filter, a sort or a change of
 * movement re-renders them without replaying anything.
 *
 * ── What this screen deliberately does NOT do ───────────────────────────────────────────────
 * There is no prefetch queue. The plan library has one because every row there costs a `/full` read
 * when it is pointed at; here the whole catalogue arrives in ONE response and the panel renders from
 * the row it already has. The only network work a row does is its own `<img loading="lazy">`.
 *
 * ── The theatre is a SIBLING of the panes, not a child of the table ─────────────────────────
 * It is a Base UI dialog portalled into `<body>`, so where it is written has no bearing on where it
 * paints — what it buys by standing here is that `useTheatre` is composed beside `useExerciseLibrary`
 * and reads the same rows, the same catalogue and the same pointer. The card is what `?cwiczenie=<id>`
 * opens, the row is what opens it, and the selection behind it is kept in step so Escape uncovers
 * the list where the trainer left it.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';

import { CreateExerciseDialog } from '~/features/create-exercise';
import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import { usePointedRow } from '~/shared/lib/usePointedRow';
import ember from '~/shared/ui/emberButton.module.css';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { ScreenHeader, ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import header from '~/shared/ui/screenHeader.module.css';
import shell from '~/shared/ui/canonScreen.module.css';

import { tagBarShape } from '../model/exerciseListStatus';
import { exerciseSortAnnouncement } from '../model/exerciseSort';
import { heardNotice, liveMessage, type HeardNotice } from '../model/liveNotice';
import { useExerciseLibrary } from '../lib/useExerciseLibrary';
import { usePanelPreference } from '../lib/usePanelPreference';
import { useExerciseActions } from '../lib/useExerciseActions';
import { useTheatre } from '../lib/useTheatre';
import {
  clearExerciseTags,
  emptyExerciseQuery,
  toggleExerciseTag,
} from '~/entities/exercise-library';

import {
  ExerciseCatalogueEmpty,
  ExerciseListError,
  ExerciseListSkeleton,
  ExerciseNoResults,
  ExerciseReviewDone,
  ExerciseScopeEmpty,
} from './ExerciseListStates';
import { ExercisePreviewPanel } from './ExercisePreviewPanel';
import { ExercisePreviewBlank, ExercisePreviewSkeleton } from './ExercisePreviewSkeleton';
import { ExerciseScopeSwitch } from './ExerciseScopeSwitch';
import { ExerciseTable } from './ExerciseTable';
import { ExerciseTagBar, ExerciseTagBarReserve } from './ExerciseTagBar';
import { TagManagerDialog } from './TagManagerDialog';
import { ExerciseTheatre } from './ExerciseTheatre';
import { ExerciseToolbar } from './ExerciseToolbar';
import styles from './ExerciseLibraryScreen.module.css';

const STAT_KEYS = ['library', 'own', 'withMaterials'] as const;

export function ExerciseLibraryScreen() {
  const { t } = useTranslation();
  const library = useExerciseLibrary();
  const panel = usePanelPreference();
  const reducedMotion = useScreenMotionPreference();

  const title = useRef<HTMLHeadingElement>(null);
  // The search input itself, so the toolbar can hand it a ref the screen also owns.
  const searchField = useRef<HTMLInputElement>(null);
  // The list card, which the filter panel hangs off: KANON puts the panel over its top-left corner,
  // so the box it is positioned against is this screen's to name.
  const listCard = useRef<HTMLElement>(null);

  // The same string as the `h1`, deliberately: the tab and the heading name one screen.
  useDocumentTitle(t('web:exerciseLibrary.documentTitle'));

  const pointer = usePointedRow(library.rows.map((row) => row.rowKey));
  const selected = library.rows.find((row) => row.rowKey === pointer.selectedKey) ?? null;

  const theatre = useTheatre({
    rows: library.rows,
    detailsById: library.detailsById,
    scope: library.scope,
    pointer,
  });

  const isReady = library.status === 'ready';
  // The wait, as the whole screen reads it. `library.status` already carries the gate's tail — the
  // gate moved into `useExerciseLibrary` on 21 September 2026 — so `loading` here means "the wait
  // branch belongs on screen", not "the request is open".
  const waiting = library.status === 'loading';

  /* What the live region says.
   *
   * The failure's own sentence while the read is down, and otherwise how many movements the table
   * is showing — the fact a filter, a search or a scope switch changes and which nothing else on
   * the screen speaks — FOLLOWED BY the order, when it is not the default.
   *
   * The order has to be in here, because `aria-sort` on a `columnheader` is read when the head is
   * reached and not when the order changes: NVDA and JAWS both stay silent on the press itself, and
   * the row count does not move when a column head is pressed. `exerciseSortAnnouncement` decides
   * whether there is anything to say; this composes the sentence, because `model/` may not
   * translate. */
  const order = exerciseSortAnnouncement(library.sort);
  const orderSentence =
    order === null
      ? ''
      : t('web:exerciseLibrary.sort.announced', {
          column: t(`web:exerciseLibrary.columns.${order.column}`),
          direction: t(
            order.descending
              ? 'web:exerciseLibrary.sort.directionDescending'
              : 'web:exerciseLibrary.sort.directionAscending',
          ),
        });
  const rowSentence = isReady
    ? t('web:exerciseLibrary.states.rowCount', { count: library.rows.length })
    : '';
  const announcement =
    library.status === 'error'
      ? t('web:exerciseLibrary.states.errorTitle')
      : [rowSentence, orderSentence].filter((part) => part !== '').join(' · ');

  const actions = useExerciseActions({
    openCard: theatre.open,
    swapCard: theatre.goTo,
    closeCardAfterRemoval: theatre.closeAfterRemoval,
  });

  /* What the live region says after an act, and when it steps aside: `model/liveNotice`. The
   * guarded `setState` during render is React's "adjust state from a previous render". */
  const [heard, setHeard] = useState<HeardNotice | null>(null);
  const nextHeard = heardNotice(actions.notice, heard, announcement);
  if (nextHeard !== heard) setHeard(nextHeard);
  const message = liveMessage(actions.notice, nextHeard, announcement);

  // The manage dialog, opened from the tag bar's „Zarządzaj" and the filter panel's „Zarządzaj
  // tagami".
  // Which control opened it, for where focus goes back to: the bar's „Zarządzaj", or the filter
  // panel's „Zarządzaj tagami" (whose panel is shut by then, and Base UI returns to „Filtry").
  // Kept past the close on purpose: the close itself is when it is read.
  const [managingTags, setManagingTags] = useState(false);
  const [tagsOpenedFrom, setTagsOpenedFrom] = useState<'bar' | 'filters'>('bar');
  const tagOptions = library.catalogue.tags;
  // One rule for the wait and the answer (`model/exerciseListStatus`'s `tagBarShape`), so the rows
  // land where the wait stood in every state.
  const tagBar = tagBarShape(library.listStatus, library.tagCount, tagOptions.length);
  const tagsShown = tagBar === 'bar';

  return (
    <main className={shell.page}>
      <motion.div className={shell.measure} {...screenMotion('rise', { reducedMotion })}>
        <ScreenHeader
          title={t('web:exerciseLibrary.title')}
          titleRef={title}
          // Never a zero while the read is open or after it failed: "0 · 0 · 0" beside a failure
          // card is three false statements about the trainer's own account.
          absence={{
            mark: t('web:exerciseLibrary.states.figureUnknown'),
            spoken: t('web:exerciseLibrary.states.figureUnknownLabel'),
          }}
          // The three words do NOT decline with their figures: they are captions under a KPI, and
          // the canon writes `w bibliotece · twoje · z materiałami` whatever the count is (sentence
          // case since the shared header dropped its capitals on 4 November 2026).
          stats={STAT_KEYS.map((key) => ({
            label: t(`web:exerciseLibrary.stats.${key}`),
            value: library.stats?.[key] ?? null,
          }))}
          actions={
            <button
              type="button"
              className={`${String(ember.ember)} ${String(header.primary)}`}
              title={t('web:exerciseLibrary.create')}
              aria-label={t('web:exerciseLibrary.create')}
              // Byte-for-byte the roster's and the plan library's ember square (owner, 18 September
              // 2026). It opens the creator on a new movement.
              onClick={() => {
                actions.openCreator('create', null);
              }}
            >
              <ScreenHeaderPlus className={header.primaryIcon} />
            </button>
          }
        />

        <div className={styles.pane}>
          <section className={styles.card} ref={listCard}>
            <ExerciseToolbar
              query={library.query}
              onQueryChange={library.setQuery}
              catalogue={library.catalogue}
              activeFacetCount={library.activeFacetCount}
              scope={library.scope}
              onScopeChange={library.setScope}
              matchCount={library.rows.length}
              disabled={!isReady}
              // `null` below the breakpoint: no column, so no control to report a state about it.
              panel={panel.fits ? { shown: panel.panel === 'shown', onToggle: panel.toggle } : null}
              anchor={listCard}
              searchRef={searchField}
              review={library.review}
              onManageTags={() => {
                setTagsOpenedFrom('filters');
                setManagingTags(true);
              }}
            />

            {/* UNDER the search row, which is the specimen's own order: the toolbar narrows what
              * the table holds, the switch says which of the three lists it is drawing, and the
              * switch's rule is what the column band hangs off. All three counts stand whatever the
              * scope is — the switch is the one control that describes the two lists the trainer is
              * NOT looking at. */}
            <ExerciseScopeSwitch
              scope={library.scope}
              onScopeChange={library.setScope}
              counts={isReady ? library.scopeCounts : null}
            />

            {/* Its room during the wait, the bar once the catalogue lands — the same rule for both,
              * so the hand-over changes ink and never pushes the table. */}
            {tagBar === 'reserve' && <ExerciseTagBarReserve skeleton={library.skeleton} />}
            {tagsShown && (
              <ExerciseTagBar
                tags={tagOptions}
                className={library.reveal}
                onToggle={(key) => {
                  library.setQuery(toggleExerciseTag(library.query, key));
                }}
                onClear={() => {
                  library.setQuery(clearExerciseTags(library.query));
                }}
                onManage={() => {
                  setTagsOpenedFrom('bar');
                  setManagingTags(true);
                }}
              />
            )}

            {/* ONE decision, in `model/exerciseListStatus`, and the column beside it reads the same
              * value — the plan library's five-way fork on this screen's own six. */}
            {library.listStatus === 'pending' ? (
              <ExerciseListSkeleton
                skeleton={library.skeleton}
                heldBack={library.tagCount === null}
                panelVisible={panel.visible}
                sort={library.sort}
                onPressColumn={library.pressColumn}
              />
            ) : library.listStatus === 'error' ? (
              <ExerciseListError onRetry={library.retry} />
            ) : library.listStatus === 'emptyCatalogue' ? (
              <ExerciseCatalogueEmpty onRetry={library.retry} />
            ) : library.listStatus === 'emptyScope' ? (
              <ExerciseScopeEmpty
                onCreate={() => {
                  actions.openCreator('create', null);
                }}
              />
            ) : library.listStatus === 'reviewDone' ? (
              <ExerciseReviewDone onShowAll={library.review.showAll} />
            ) : library.listStatus === 'filtered' ? (
              <ExerciseNoResults
                term={library.query.text.trim()}
                facets={library.activeFacetCount > 0 || library.review.only}
                otherLanguageFailed={library.otherLanguageFailed}
                onClear={() => {
                  // Everything that narrows the list, the „Do uzupełnienia" toggle included.
                  library.setQuery(emptyExerciseQuery());
                  library.review.showAll();
                }}
              />
            ) : (
              <ExerciseTable
                rows={library.rows}
                pointer={pointer}
                sort={library.sort}
                onPressColumn={library.pressColumn}
                panelVisible={panel.visible}
                className={library.reveal}
                onOpen={theatre.open}
              />
            )}

            {/* The screen's ONE live region, always mounted and empty when there is nothing to say.
              * A `role="status"` inserted together with its text is a region that did not exist when
              * the change happened, which some screen readers do not announce at all. */}
            <LiveRegionAnnouncer message={message} />
          </section>

          {/* The column stands from the first frame, in EVERY state, so the list card never changes
            * width under the trainer: its skeleton while the catalogue is on its way, the movement
            * once it has landed, and nothing at all beside a failure or an empty list — blank, at
            * its own width (polish of 4 November 2026; before it the card widened from 976px to
            * 1388px the moment a search matched nothing).
            *
            * ONE wrapper for all three, and that is the fix rather than the tidier spelling. The wait
            * used to be a plain `div` and the answer a `motion.div` on `rise`, so React swapped the
            * element type when the catalogue landed and the loaded column REPLAYED the entrance.
            * Now the column rises once, with the measure, and its contents swap inside a box that
            * has not moved since the first frame. The wrapper is not keyed and `waiting` is the
            * GATE's answer, not the read's. */}
          {panel.visible && (
            <motion.div
              className={styles.side}
              {...screenMotion('rise', { reducedMotion, index: 1 })}
            >
              {waiting ? (
                <ExercisePreviewSkeleton skeleton={library.skeleton} />
              ) : isReady && selected !== null ? (
                <ExercisePreviewPanel row={selected} />
              ) : (
                <ExercisePreviewBlank />
              )}
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Portalled into `<body>` by Base UI, so it paints over the whole shell rather than inside
        * the measure. It draws nothing at all until the address names a movement the catalogue
        * holds. */}
      <ExerciseTheatre theatre={theatre} actions={actions} />

      {/* The creator on a NEW movement, opened by the header's `+` or the empty `Twoje`. Its edit
        * and duplicate twin nests inside the card instead — see `ExerciseTheatre`. Rendered
        * unconditionally: `CreateExerciseDialog` says why. */}
      <TagManagerDialog
        open={managingTags}
        onClose={() => {
          setManagingTags(false);
        }}
        items={library.items}
        // Opened from the bar and every tag deleted: the bar is gone with its „Zarządzaj", so
        // focus goes to the search field above where it stood. Otherwise Base UI's own return.
        finalFocus={() => (tagsOpenedFrom === 'bar' && !tagsShown ? searchField.current : true)}
      />

      <CreateExerciseDialog
        open={actions.creatorOpen && actions.creatorRequest?.mode === 'create'}
        request={actions.creatorRequest?.mode === 'create' ? actions.creatorRequest : null}
        onClose={actions.closeCreator}
        onSaved={actions.onCreatorSaved}
        finalFocus={actions.creatorFinalFocus}
      />
    </main>
  );
}
